import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormLabel,
	InputLabel,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
} from '@mui/material';
import { EntityType, type GridEntry, type PropertyDefinition, type Repository } from '../types';
import { buildGridAxisPropertyFields, formPrimarySecondaryLayoutFromGrid } from '../utils/resolveGridAxisSnap';
import { useGetRepositoryByIdQuery } from '../services/repositoryEntries';
import { useAppSelector } from '../hooks';
import { selectIsAuthenticated } from '../containers/authSlice';
import { selectAllPropertyDefinitions } from '../containers/contentSlice';


export interface EditGridDialogProps {
	open: boolean;
	grid: GridEntry | null;
	repositories: Repository[];
	onClose: () => void;
	onSubmit: (payload: {
		id: string;
		repositoryId?: string;
		primaryPropertyId?: string | null;
		secondaryPropertyId?: string | null;
		direction?: string | null;
	}) => void;
}

/** When true, primary grouping uses the horizontal (X) axis with swimlanes; secondary maps to Y. */
const defaultPrimaryLayoutVertical = true;

function axisPropertiesForCollection(
	collectionRepositoryId: string,
	definitions: PropertyDefinition[],
): PropertyDefinition[] {
	if (!collectionRepositoryId) {
		return [];
	}
	return definitions.filter(
		(d) =>
			d.parentId === collectionRepositoryId
			&& (d.type === EntityType.PropertyDefinitionBelongsTo || d.type === EntityType.PropertyDefinitionHas),
	);
}

const EditGridDialog: React.FC<EditGridDialogProps> = ({
	open,
	grid,
	repositories,
	onClose,
	onSubmit,
}) => {
	const [repositoryId, setRepositoryId] = useState('');
	const [primaryAxisPropertyId, setPrimaryAxisPropertyId] = useState('');
	const [primaryLayoutVertical, setPrimaryLayoutVertical] = useState(defaultPrimaryLayoutVertical);
	const [secondaryAxisPropertyId, setSecondaryAxisPropertyId] = useState('');
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);

	const collectionRepositoryId = open ? (repositoryId || grid?.repositoryId || '') : '';

	const { data: repositoryById } = useGetRepositoryByIdQuery(collectionRepositoryId, {
		skip: !open || !collectionRepositoryId || !isAuthenticated,
	});

	const localPropertyDefinitions = useMemo(
		() => allPropertyDefinitions.filter((def) => def.parentId === collectionRepositoryId),
		[allPropertyDefinitions, collectionRepositoryId],
	);

	const definitionsForAxis = useMemo(
		() => (isAuthenticated ? (repositoryById?.propertyDefinitions ?? []) : localPropertyDefinitions),
		[isAuthenticated, repositoryById?.propertyDefinitions, localPropertyDefinitions],
	);

	const axisOptions = useMemo(
		() => axisPropertiesForCollection(collectionRepositoryId, definitionsForAxis),
		[collectionRepositoryId, definitionsForAxis],
	);

	useLayoutEffect(() => {
		if (!open || !grid) {
			return;
		}
		setRepositoryId(grid.repositoryId ?? '');
		const axis = formPrimarySecondaryLayoutFromGrid(grid);
		setPrimaryAxisPropertyId(axis.primaryAxisPropertyId);
		setSecondaryAxisPropertyId(axis.secondaryAxisPropertyId);
		setPrimaryLayoutVertical(axis.primaryLayoutVertical);
	}, [grid, open, repositories]);

	useEffect(() => {
		if (!primaryAxisPropertyId && !secondaryAxisPropertyId) {
			return;
		}
		const validIds = new Set(axisOptions.map((d) => d.id));
		if (primaryAxisPropertyId && !validIds.has(primaryAxisPropertyId)) {
			setPrimaryAxisPropertyId('');
		}
		if (secondaryAxisPropertyId && !validIds.has(secondaryAxisPropertyId)) {
			setSecondaryAxisPropertyId('');
		}
	}, [axisOptions, primaryAxisPropertyId, secondaryAxisPropertyId]);

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (!grid) {
			return;
		}
		const axisFields = buildGridAxisPropertyFields({
			primaryLayoutVertical,
			primaryAxisPropertyId,
			secondaryAxisPropertyId,
		});
		onSubmit({
			id: grid.id,
			repositoryId: repositoryId || undefined,
			...axisFields,
		});
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth scroll="body">
			<form onSubmit={handleSubmit} autoComplete="off">
				<DialogTitle>Edit grid</DialogTitle>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} mt={1}>
						<FormControl fullWidth size="small">
							<InputLabel id="edit-grid-repository-label">Collection</InputLabel>
							<Select
								labelId="edit-grid-repository-label"
								label="Collection"
								value={repositoryId}
								onChange={(e) => setRepositoryId(String(e.target.value))}
							>
								<MenuItem value="">
									<em>None</em>
								</MenuItem>
								{repositories.map((r) => (
									<MenuItem key={`grid-repo-${r.id}`} value={r.id}>
										{r.title}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<Box display="flex" flexDirection="column" gap={1.5}>
							<FormControl fullWidth size="small">
								<InputLabel id="edit-grid-primary-axis-label">Primary grouping by</InputLabel>
								<Select
									labelId="edit-grid-primary-axis-label"
									label="Primary grouping by"
									value={primaryAxisPropertyId}
									onChange={(e) => setPrimaryAxisPropertyId(String(e.target.value))}
									disabled={!repositoryId || axisOptions.length === 0}
								>
									<MenuItem value="">
										<em>None</em>
									</MenuItem>
									{axisOptions.map((d) => (
										<MenuItem key={`primary-prop-${d.id}`} value={d.id}>
											{d.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<FormControl component="fieldset" variant="standard" sx={{ m: 0 }}>
								<FormLabel component="legend" sx={{ typography: 'body2', color: 'text.primary', mb: 0.5 }}>
									Layout
								</FormLabel>
								<RadioGroup
									row
									value={primaryLayoutVertical ? 'vertical' : 'horizontal'}
									onChange={(e) => setPrimaryLayoutVertical(e.target.value === 'vertical')}
								>
									<FormControlLabel
										value="vertical"
										control={<Radio size="small" />}
										label="Columns"
									/>
									<FormControlLabel
										value="horizontal"
										control={<Radio size="small" />}
										label="Rows"
									/>
								</RadioGroup>
							</FormControl>
						</Box>

					</Box>
				</DialogContent>
				<DialogActions>
					<Button type="button" onClick={onClose}>
						Cancel
					</Button>
					<Button type="submit" variant="contained">
						Save
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default EditGridDialog;
